import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { ChallanService } from '@/services/challanService'
import { getLoginUserEmail, getLoginUserId, getLoginUserMobile, getRoles } from '@/lib/auth'
import { asRecord, asString, asStringArray, getApiErrorMessage } from '@/utils/data'
import { OneTimePasswordReveal, type OneTimeAccountDetail } from '@/components/common/OneTimePasswordReveal'

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const accountId = getLoginUserId()
  const accountEmail = getLoginUserEmail()
  const accountMobile = getLoginUserMobile()
  const [passwordMode, setPasswordMode] = useState<'generated' | 'chosen'>('chosen')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [confirmError, setConfirmError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [formError, setFormError] = useState('')
  const [loading, setLoading] = useState(false)
  const [passwordReveal, setPasswordReveal] = useState<{ password: string; notice?: string; account: OneTimeAccountDetail } | null>(null)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setConfirmError('')
    setPasswordError('')
    setFormError('')

    if (!accountId) return setFormError('Your account identity is unavailable. Please sign in again.')
    if (passwordMode === 'chosen') {
      if (!newPassword) return setPasswordError('Enter a new password.')
      if (newPassword.length < 6) return setPasswordError('Password must be at least 6 characters.')
      if (newPassword !== confirmPassword) return setConfirmError('Passwords do not match.')
    }

    setLoading(true)
    try {
      const response = await ChallanService.resetOpsAccountPassword(accountId, passwordMode === 'chosen' ? newPassword : undefined)
      const root = asRecord(response)
      const detail = asRecord(root.detail)
      const returnedPassword = asString(root.generatedPassword) || (passwordMode === 'chosen' ? newPassword : '')
      if (!returnedPassword) throw new Error('Password reset response did not include the generated password')
      setPasswordReveal({
        password: returnedPassword,
        notice: asString(root.passwordNotice) || 'Save this password securely. It is shown only once.',
        account: {
          name: asString(detail.name) || accountEmail,
          email: asString(detail.email) || accountEmail,
          mobile: asString(detail.mobile) || accountMobile,
          roles: asStringArray(detail.roles).length ? asStringArray(detail.roles) : getRoles(),
        },
      })
    } catch (error) {
      setFormError(getApiErrorMessage(error, 'Unable to change password. Please try again.'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-bg px-4 py-8 sm:px-8">
      <div className="w-full max-w-xl">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-md bg-primary font-semibold text-primary-on">OP</div>
          <div>
            <p className="font-semibold text-text">Fulfillment Panel</p>
            <p className="text-text-muted">Operations workspace</p>
          </div>
        </div>

        <form onSubmit={submit} noValidate className="flex flex-col gap-5 rounded-lg border border-border bg-surface p-6 sm:p-8">
          <div>
            <h1 className="text-lg font-semibold text-text">Change your password</h1>
            <p className="mt-1 text-text-secondary">Update the password for {accountEmail || 'your account'}.</p>
          </div>

          <p className="rounded-md border border-border bg-surface-sunken px-3 py-2 text-text-secondary">
            Signed in as <span className="font-semibold text-text">{accountEmail || 'current user'}</span>
            {accountMobile ? <span> · {accountMobile}</span> : null}
          </p>

          <div className="grid gap-2" role="radiogroup" aria-label="Password reset mode">
            <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 has-[:checked]:border-primary has-[:checked]:bg-primary-subtle">
              <input type="radio" name="self-password-mode" checked={passwordMode === 'generated'} onChange={() => setPasswordMode('generated')} className="mt-0.5 size-4 accent-primary" />
              <span><span className="block font-semibold text-text">Generate a secure password</span><span className="block text-text-secondary">The server creates a one-time password for you.</span></span>
            </label>
            <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 has-[:checked]:border-primary has-[:checked]:bg-primary-subtle">
              <input type="radio" name="self-password-mode" checked={passwordMode === 'chosen'} onChange={() => setPasswordMode('chosen')} className="mt-0.5 size-4 accent-primary" />
              <span><span className="block font-semibold text-text">Choose a password</span><span className="block text-text-secondary">Use at least 6 characters.</span></span>
            </label>
          </div>

          {passwordMode === 'chosen' ? (
            <div className="grid gap-4 sm:grid-cols-2">
            <FormField id="new-password" label="New password" required error={passwordError}>
              {(controlProps) => (
                <div className="relative">
                  <Input {...controlProps} type={showNewPassword ? 'text' : 'password'} autoComplete="new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} disabled={loading} className="pr-11" />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-md text-text-secondary hover:text-text focus-visible:outline-none focus-visible:shadow-focus"
                    onClick={() => setShowNewPassword((value) => !value)}
                    aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                    aria-pressed={showNewPassword}
                  >
                    {showNewPassword ? <EyeOff className="size-4" aria-hidden="true" /> : <Eye className="size-4" aria-hidden="true" />}
                  </button>
                </div>
              )}
            </FormField>
            <FormField id="confirm-password" label="Confirm password" required error={confirmError}>
              {(controlProps) => (
                <div className="relative">
                  <Input {...controlProps} type={showConfirmPassword ? 'text' : 'password'} autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} disabled={loading} className="pr-11" />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-md text-text-secondary hover:text-text focus-visible:outline-none focus-visible:shadow-focus"
                    onClick={() => setShowConfirmPassword((value) => !value)}
                    aria-label={showConfirmPassword ? 'Hide confirmation password' : 'Show confirmation password'}
                    aria-pressed={showConfirmPassword}
                  >
                    {showConfirmPassword ? <EyeOff className="size-4" aria-hidden="true" /> : <Eye className="size-4" aria-hidden="true" />}
                  </button>
                </div>
              )}
            </FormField>
            </div>
          ) : null}

          {formError ? <Alert tone="error">{formError}</Alert> : null}

          <Button type="submit" loading={loading} className="w-full">
            {loading ? 'Changing password' : 'Change password'}
          </Button>
        </form>
      </div>
      {passwordReveal ? <OneTimePasswordReveal password={passwordReveal.password} notice={passwordReveal.notice} account={passwordReveal.account} onDismiss={() => { setPasswordReveal(null); setNewPassword(''); setConfirmPassword(''); navigate('/', { replace: true }) }} /> : null}
    </div>
  )
}
